import React, { useMemo } from 'react';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from 'primereact/skeleton';
import { mentorDevelopmentApi } from 'api';
import { Trans } from 'react-i18next';
import { usePanel } from 'contexts/panelContext';
import { useProgramTranslation } from 'hooks/useProgramTranslation';
import { mentorDevelopmentProgressSummaryQueryKey } from 'components/userPanel/mentorDevelopment/useMentorDevelopment';
import ProgressSteps, { ProgressStepItem } from './ProgressSteps';

const mentorDashboardStepKeys = [
	'currentCompetencyAssessment',
	'selectedDevelopmentAreas',
	'developmentPlanExecution',
	'finalCompetencyAssessment',
] as const;

const mentorDevelopmentStageHashMap = {
	currentCompetencyAssessment: '#stage1',
	selectedDevelopmentAreas: '#stage2',
	developmentPlanExecution: '#stage3',
	finalCompetencyAssessment: '#stage4',
} as const;

const MentorProgressSteps = () => {
	const { tProgram } = useProgramTranslation();
	const { currentApplicationData, panelType, currentProgram, panelScopePath } = usePanel();

	const shouldRender = panelType === 'mentor' && currentProgram.programType !== 'coaching';

	const { data, isLoading, isError, error } = useQuery(
		mentorDevelopmentProgressSummaryQueryKey(currentApplicationData.id),
		() => mentorDevelopmentApi.getProgressSummary(currentApplicationData.id),
		{
			enabled: shouldRender && Boolean(currentApplicationData.id),
		}
	);

	const steps = useMemo<ProgressStepItem[]>(() => {
		if (!data) {
			return [];
		}

		return mentorDashboardStepKeys.map((stepKey) => ({
			id: stepKey,
			label: tProgram(`userPanel.progressSteps.mentor.steps.${stepKey}`),
			href: `${panelScopePath}/mentor-development`,
			hash: mentorDevelopmentStageHashMap[stepKey],
		}));
	}, [data, panelScopePath, tProgram]);

	if (!shouldRender) {
		return null;
	}

	if (isLoading) {
		return <Skeleton className="w-full" height="12rem" borderRadius="16px" />;
	}

	if (isError) {
		const status = error instanceof AxiosError ? error.response?.status : undefined;

		if (status === 403 || status === 404) {
			return null;
		}

		return null;
	}

	if (!data || !steps.length) {
		return null;
	}

	return (
		<ProgressSteps
			title={
				<Trans t={tProgram as any} i18nKey="userPanel.progressSteps.mentor.title" components={[<strong />]} />
			}
			variant="mentor-white"
			steps={steps}
			activeStep={data.activeStep}
			action={{
				label: tProgram('userPanel.progressSteps.mentor.action'),
				to: `${panelScopePath}/mentor-development`,
			}}
		/>
	);
};

export default MentorProgressSteps;
